"use client";

import { Suspense, useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { createClient } from "@/lib/supabase/client";
import Link from "next/link";
import { Logo } from "@/components/brand/Logo";
import { ALL_EXAMS } from "@/app/landing-data";
import { MailCheck, AlertTriangle } from "lucide-react";

/* ── Google SVG icon ── */
function GoogleIcon() {
  return (
    <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

/* ── Mini score-card preview (mirrors the homepage hero card) ─────────────── */
const PREVIEW_SECTIONS = [
  { name: "Reading",   pct: 93,  val: 28, c: "from-blue-600 to-blue-700" },
  { name: "Listening", pct: 96,  val: 29, c: "from-blue-600 to-blue-700" },
  { name: "Speaking",  pct: 83,  val: 25, c: "from-emerald-500 to-emerald-600" },
  { name: "Writing",   pct: 100, val: 30, c: "from-emerald-500 to-emerald-600" },
];

function AuthForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirect = searchParams.get("redirect") ?? "/dashboard";
  const defaultMode = searchParams.get("mode") === "signup" ? "signup" : "login";
  const oauthError = searchParams.get("error");
  const oauthErrorCode = searchParams.get("error_code");

  const [mode, setMode] = useState<"login" | "signup" | "forgot">(defaultMode as "login" | "signup");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  // CF-17: friendly message for the common OAuth-state-expired case
  // (?error=invalid_request&error_code=bad_oauth_state). Without this,
  // users saw Supabase's raw "OAuth state not found or expired" string
  // and didn't know what to do.
  const [error, setError] = useState<string | null>(
    oauthError === "oauth_cancelled" ? "Sign-in was cancelled. Please try again." :
    oauthErrorCode === "bad_oauth_state" ? "Your sign-in link expired. Please click the button below to try again — it only takes a moment." :
    oauthError ? "Sign-in failed. Please try again." : null
  );
  const [message, setMessage] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  const supabase = createClient();

  // If a user is already logged in when they hit /auth (e.g. clicked a stale
  // sign-in link, opened a new tab to switch accounts), send them somewhere
  // sensible. Honor an explicit `?redirect=` only when it's a generic page
  // (not a specific exam-session URL) — otherwise we'd dump them mid-exam
  // unexpectedly. Default to /dashboard.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (cancelled || !user) return;
      const target = redirect && !redirect.match(/\/(exam|report)\/[^/]+/) ? redirect : "/dashboard";
      router.replace(target);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (mode === "forgot") {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${location.origin}/auth/update-password`,
      });
      if (error) setError(error.message);
      else setMessage("We've sent a password reset link to your email.");
    } else if (mode === "signup") {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { display_name: name },
          emailRedirectTo: `${location.origin}/auth/callback`,
        },
      });
      if (error) {
        setError(error.message);
      } else if (data.user && (data.user.identities?.length ?? 0) === 0) {
        // Supabase returns a fake user (no identities) when the email already exists.
        // This is anti-enumeration behaviour — we detect it and tell the user.
        setError(
          "This email is already registered. Please sign in, or use \"Forgot password?\" if you can't remember it."
        );
      } else {
        setMessage("Check your email to confirm your account.");
      }
    } else {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) setError(error.message);
      else router.push(redirect);
    }
    setLoading(false);
  }

  async function handleResend() {
    if (resendCooldown > 0 || !email) return;
    const { error } = await supabase.auth.resend({
      type: "signup",
      email,
      options: { emailRedirectTo: `${location.origin}/auth/callback` },
    });
    if (error) {
      setError(error.message);
    } else {
      setResendCooldown(60);
      const t = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) { clearInterval(t); return 0; }
          return prev - 1;
        });
      }, 1000);
    }
  }

  async function handleGoogle() {
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${location.origin}/auth/callback?redirect=${redirect}` },
    });
  }

  /* Mode-aware headline: <main> + italic-serif <accent>. Mirrors the
     homepage hero treatment ("Practice until perfect is normal."). */
  const headline =
    mode === "signup"
      ? { main: "Practice until",   accent: "perfect.",     sub: "Full-length exams with AI scoring, per-question review, and detailed writing feedback." }
    : mode === "forgot"
      ? { main: "Reset your",       accent: "password.",     sub: "Enter your email and we'll send you a secure reset link." }
      : { main: "Welcome",          accent: "back.",         sub: "Pick up where you left off — your progress is saved across sessions." };

  return (
    <div className="min-h-screen flex bg-cream">

      {/* ─── Left panel: editorial branding (matches landing hero) ─────────── */}
      <div className="hidden lg:flex flex-col w-[480px] xl:w-[560px] flex-shrink-0 px-12 py-10 relative overflow-hidden border-r border-cream">
        {/* Warm radial wash — same as homepage hero */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_50%_at_50%_-10%,rgba(99,102,241,0.10),transparent)] pointer-events-none" />
        {/* Subtle bottom gradient so the score card lifts off */}
        <div className="absolute inset-x-0 bottom-0 h-[40%] bg-[linear-gradient(180deg,transparent,rgba(255,255,255,0.4))] pointer-events-none" />

        <div className="relative z-10 flex flex-col h-full">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
            <Logo size={32} />
            <span className="font-bold text-[17px] tracking-tight text-[var(--text-primary)]">FullPracticeTests</span>
          </Link>

          {/* Hero block */}
          <div className="mt-14">
            {/* Beta pill — identical treatment to homepage hero */}
            <div className="inline-flex items-center gap-2 bg-indigo-tint text-indigo-brand text-[11px] font-semibold px-3 py-1.5 rounded-full mb-6 tracking-wide">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full pulse-dot" />
              Free during beta · AI-scored
            </div>

            <h1 className="text-[42px] xl:text-[46px] font-extrabold leading-[1.05] tracking-[-0.025em] text-[var(--text-primary)]">
              {headline.main}<br />
              <span className="font-serif-italic text-indigo-brand">{headline.accent}</span>
            </h1>

            <p className="mt-5 text-[15px] leading-relaxed text-[var(--text-muted)] max-w-[380px]">
              {headline.sub}
            </p>
          </div>

          {/* Mini score-card preview (mirrors homepage hero card) */}
          <div className="mt-10 bg-white rounded-2xl p-5 shadow-cream border border-cream relative">
            {/* Floating "Top 8%" chip */}
            <div className="absolute -top-2.5 -right-2.5 bg-white text-[10px] font-semibold px-2.5 py-1 rounded-full shadow border border-cream flex items-center gap-1.5 text-emerald-700">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/>
              </svg>
              Top 8% globally
            </div>

            {/* Header chip */}
            <div className="flex items-center gap-2 mb-4">
              <span className="bg-indigo-tint text-indigo-brand text-[10px] font-bold px-2 py-0.5 rounded-md tracking-wide">TOEFL iBT</span>
              <span className="text-[11px] text-[var(--text-muted)] font-medium">Practice Test #3</span>
            </div>

            {/* Ring + summary */}
            <div className="flex items-center gap-4 mb-4">
              <div className="relative w-[76px] h-[76px] flex-shrink-0">
                <svg width="76" height="76" viewBox="0 0 96 96">
                  <circle cx="48" cy="48" r="40" stroke="#EEECEA" strokeWidth="7" fill="none"/>
                  <circle cx="48" cy="48" r="40" stroke="url(#authRingG)" strokeWidth="7" fill="none"
                    strokeDasharray="234.6 251.3" strokeLinecap="round" transform="rotate(-90 48 48)"/>
                  <defs>
                    <linearGradient id="authRingG" x1="96" y1="0" x2="0" y2="96" gradientUnits="userSpaceOnUse">
                      <stop offset="0%" stopColor="#2563EB"/>
                      <stop offset="100%" stopColor="#10B981"/>
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="text-[22px] font-extrabold leading-none">112</div>
                  <div className="text-[9px] text-[var(--text-muted)] font-medium mt-0.5">/ 120</div>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] text-[var(--text-muted)] font-semibold uppercase tracking-wider">Total Score</div>
                <div className="text-[12px] text-[var(--text-secondary)] mt-2 leading-snug">
                  Reading, Listening, Speaking &amp; Writing — scored by AI in seconds.
                </div>
              </div>
            </div>

            {/* Section bars */}
            <div className="space-y-2 pt-3 border-t border-cream">
              {PREVIEW_SECTIONS.map(b => (
                <div key={b.name} className="grid grid-cols-[72px_1fr_24px] items-center gap-3 text-[11px]">
                  <span className="text-[var(--text-secondary)] font-medium">{b.name}</span>
                  <div className="h-1 bg-[var(--border-light)] rounded-full overflow-hidden">
                    <div className={`h-full bg-gradient-to-r ${b.c} rounded-full`} style={{ width: `${b.pct}%` }} />
                  </div>
                  <span className="font-bold text-right tabular-nums">{b.val}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Stat row at bottom (matches homepage hero stats) */}
          <div className="mt-auto pt-10 flex items-center gap-6">
            <div>
              <div className="text-[20px] font-extrabold tracking-tight">{ALL_EXAMS.length}+</div>
              <div className="text-[10px] text-[var(--text-muted)] font-medium uppercase tracking-wider mt-0.5">Exams</div>
            </div>
            <div className="w-px h-7 bg-[var(--border)]" />
            <div>
              <div className="text-[20px] font-extrabold tracking-tight">10K+</div>
              <div className="text-[10px] text-[var(--text-muted)] font-medium uppercase tracking-wider mt-0.5">Questions</div>
            </div>
            <div className="w-px h-7 bg-[var(--border)]" />
            <div>
              <div className="text-[20px] font-extrabold tracking-tight">AI</div>
              <div className="text-[10px] text-[var(--text-muted)] font-medium uppercase tracking-wider mt-0.5">Essay scoring</div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Right panel: form — sits on a white card for elevation ──────── */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 lg:py-10 bg-cream relative">
        {/* Mobile-only logo */}
        <Link href="/" className="flex items-center gap-2 mb-8 lg:hidden">
          <Logo size={28} />
          <span className="font-bold text-[var(--text-primary)]">FullPracticeTests</span>
        </Link>

        <div className="w-full max-w-[400px] bg-white border border-cream rounded-2xl shadow-cream-lg px-7 py-8 lg:px-8 lg:py-9">
          <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-indigo-brand mb-3">
            {mode === "signup" ? "New here" : mode === "forgot" ? "Account recovery" : "Sign in"}
          </div>
          <h2 className="text-[26px] font-extrabold tracking-[-0.02em] text-[var(--text-primary)] mb-1.5 leading-tight">
            {mode === "signup" ? "Create your account" : mode === "forgot" ? "Reset your password" : "Sign in to your account"}
          </h2>
          <p className="text-[13px] text-[var(--text-muted)] mb-6">
            {mode === "signup"
              ? "Already have an account? "
              : mode === "forgot"
              ? "Remembered it? "
              : "Don't have an account? "}
            <button
              onClick={() => { setMode(mode === "signup" ? "login" : "signup"); setError(null); setMessage(null); }}
              className="text-indigo-brand hover:opacity-80 font-semibold"
            >
              {mode === "signup" ? "Sign in" : mode === "forgot" ? "Sign in" : "Sign up free"}
            </button>
          </p>

          {/* Google button */}
          {mode !== "forgot" && (
            <>
              <button
                onClick={handleGoogle}
                className="w-full flex items-center justify-center gap-2.5 border border-cream bg-white text-[var(--text-primary)] font-semibold py-2.5 rounded-xl hover:border-indigo-brand/40 hover:bg-cream-2/30 transition-all text-[13px] mb-4"
              >
                <GoogleIcon />
                Continue with Google
              </button>

              {/* Divider */}
              <div className="flex items-center gap-3 mb-5">
                <div className="flex-1 h-px bg-[var(--border-light)]" />
                <span className="text-[10px] text-[var(--text-faint)] font-semibold uppercase tracking-wider">or with email</span>
                <div className="flex-1 h-px bg-[var(--border-light)]" />
              </div>
            </>
          )}

          {message ? (
            <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-5 text-center space-y-3">
              <div><MailCheck className="w-6 h-6 text-green-600 inline-block" strokeWidth={1.75} aria-hidden="true" /></div>
              <div>
                <p className="text-sm font-semibold text-green-800 mb-1">
                  {mode === "signup" ? "Confirm your email" : "Check your email"}
                </p>
                <p className="text-xs text-green-700">{message}</p>
              </div>
              {mode === "signup" && (
                <>
                  <p className="text-[11px] text-gray-500 leading-relaxed">
                    Can&apos;t find it? Check your <strong>spam / junk</strong> folder,
                    or request a new link below.
                  </p>
                  {error && (
                    <p className="text-xs text-red-600">{error}</p>
                  )}
                  <button
                    onClick={handleResend}
                    disabled={resendCooldown > 0}
                    className="text-xs text-indigo-brand hover:opacity-80 font-semibold disabled:text-gray-400 disabled:cursor-not-allowed transition-opacity"
                  >
                    {resendCooldown > 0
                      ? `Resend available in ${resendCooldown}s`
                      : "Resend confirmation email →"}
                  </button>
                </>
              )}
              {mode === "forgot" && (
                <p className="text-[11px] text-gray-500 leading-relaxed">
                  Can&apos;t find it? Check your <strong>spam / junk</strong> folder.
                  The link expires in 1 hour.
                </p>
              )}
            </div>
          ) : mode === "forgot" ? (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[var(--text-muted)] mb-1.5">Email address</label>
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="w-full border border-[var(--border-light)] rounded-xl px-3.5 py-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-indigo-brand/30 focus:border-indigo-brand transition-colors bg-white text-[var(--text-primary)] placeholder:text-[var(--text-faint)]"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-3.5 py-2.5 text-xs text-red-700 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" strokeWidth={1.75} aria-hidden="true" />
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[var(--text-primary)] text-[var(--bg-primary)] font-bold py-2.5 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 text-sm shadow-sm mt-1"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                    </svg>
                    Sending…
                  </span>
                ) : (
                  "Send reset link →"
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              {mode === "signup" && (
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-wider text-[var(--text-muted)] mb-1.5">Your name</label>
                  <input
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    required
                    autoComplete="name"
                    className="w-full border border-[var(--border-light)] rounded-xl px-3.5 py-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-indigo-brand/30 focus:border-indigo-brand transition-colors bg-white text-[var(--text-primary)] placeholder:text-[var(--text-faint)]"
                  />
                </div>
              )}
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[var(--text-muted)] mb-1.5">Email address</label>
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="w-full border border-[var(--border-light)] rounded-xl px-3.5 py-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-indigo-brand/30 focus:border-indigo-brand transition-colors bg-white text-[var(--text-primary)] placeholder:text-[var(--text-faint)]"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[11px] font-bold uppercase tracking-wider text-[var(--text-muted)]">Password</label>
                  {mode === "login" && (
                    <button
                      type="button"
                      onClick={() => { setMode("forgot"); setError(null); setMessage(null); }}
                      className="text-xs text-indigo-brand hover:opacity-80 font-semibold"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
                <input
                  type="password"
                  placeholder={mode === "signup" ? "At least 8 characters" : "••••••••"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete={mode === "signup" ? "new-password" : "current-password"}
                  className="w-full border border-[var(--border-light)] rounded-xl px-3.5 py-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-indigo-brand/30 focus:border-indigo-brand transition-colors bg-white text-[var(--text-primary)] placeholder:text-[var(--text-faint)]"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-3.5 py-2.5 text-xs text-red-700 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" strokeWidth={1.75} aria-hidden="true" />
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[var(--text-primary)] text-[var(--bg-primary)] font-bold py-2.5 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 text-sm shadow-sm mt-1"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                    </svg>
                    {mode === "signup" ? "Creating account…" : "Signing in…"}
                  </span>
                ) : (
                  mode === "signup" ? "Create free account →" : "Sign in →"
                )}
              </button>
            </form>
          )}

          <p className="mt-6 text-center text-[11px] text-[var(--text-faint)] leading-relaxed">
            By continuing you agree to our{" "}
            <Link href="/terms" className="hover:text-[var(--text-secondary)] underline underline-offset-2">Terms</Link>
            {" "}and{" "}
            <Link href="/privacy" className="hover:text-[var(--text-secondary)] underline underline-offset-2">Privacy Policy</Link>.
          </p>
        </div>

        {/* Footer "← Back to home" — quiet escape hatch */}
        <Link
          href="/"
          className="mt-8 text-[12px] font-semibold text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
        >
          ← Back to home
        </Link>
      </div>

    </div>
  );
}

export default function AuthPage() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-cream" />}>
      <AuthForm />
    </Suspense>
  );
}
